/*
 *
 *  * Copyright(c) 2022 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */

import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { VirtualListComponent } from '../virtual-list/virtual-list.component';

@Component({
  selector: 'wfm-virtual-list-dropdown',
  templateUrl: './virtual-list-dropdown.component.html'
})
export class VirtualListDropdownComponent implements OnChanges {
  @Input() items: Array<any> = [];
  @Input() placeholder = '';
  @Input() disabled = false;
  @Input() multiple = false;
  @Input() labelField;
  @Input() enableInternalSearch = true;
  @Input() searchLabel;
  @Input() searchIcon = 'ico-filter-16';
  @Input() selectionThreshold = 0;
  @Input() value = [];
  @Input() isShowSelected = true;
  @Input() disableByField: string | null = null;

  @Output() errorNotify = new EventEmitter<boolean>();
  @Output() queryStr = new EventEmitter();
  @Output() dropdownOpen = new EventEmitter();
  @Output() valueChange = new EventEmitter<any>();

  @ViewChild('trigger') trigger: ElementRef;
  @ViewChild(VirtualListComponent) virtualListComponent: VirtualListComponent;

  isOpen = false;
  error = false;

  private window: Window;
  private mouseUpEvent: () => void;
  private $minWidth = 0;

  constructor(@Inject(DOCUMENT) private document, private renderer: Renderer2) {
    this.window = this.document.defaultView;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.value && !changes.value.firstChange && changes.value.currentValue !== changes.value.previousValue) {
      this.sendSelectionThresholdErrorNotification();
    }
  }

  get isListOfObjects(): boolean {
    return typeof this.items[0] === 'object';
  }

  get minWidth(): number {
    const _minWidth = this.$minWidth;
    this.$minWidth = this.trigger?.nativeElement.offsetWidth || 0;
    return _minWidth;
  }

  isOpenChange() {
    this.isOpen && this.window.setTimeout(() => this.virtualListComponent?.focusInput(), 0);
    this.isOpen && this.dropdownOpen.emit();
  }

  open() {
    if (!this.isOpen && !this.mouseUpEvent && !this.disabled) {
      this.isOpen = true;
      this.isOpenChange();
    }
  }

  mousedown() {
    if (this.isOpen) {
      this.mouseUpEvent = this.renderer.listen('document', 'mouseup', () => {
        this.mouseUpEvent();
        this.window.setTimeout(() => {
          this.mouseUpEvent = null;
        }, 0);
      });
    }
    this.dropdownOpen.emit();
  }

  getSelectedItems(items: any[]) {
    if (!this.multiple) {
      this.isOpen = false;
    }
    this.value = items;
    this.sendSelectionThresholdErrorNotification();
    this.valueChange.emit(this.value);
  }

  private sendSelectionThresholdErrorNotification() {
    if (this.selectionThreshold > 0) {
      this.error = this.value?.length > this.selectionThreshold;
      this.errorNotify.emit(this.error);
    }
  }
}
